import { useState, useEffect, useRef } from "react";
import { createBrowserRouter, RouterProvider, Outlet, Navigate } from 'react-router-dom'
import { useAuth, AuthProvider } from "./contexts/AuthContext";
import analytics from './config/tools';
import Home from './pages/Home'
import Signin from './pages/Signin'
import Signin2 from "./pages/Signin2";
import Signup from './pages/Signup'
import Users from './pages/Users'
import Groups from './pages/Groups'
import StudentsRegistered from './pages/StudentsRegistered'
import StudentsWaitingReenrollment from "./pages/StudentsWaitingReenrollment"
import StudentsFinishedReenrollment from "./pages/StudentsFinishedReenrollment"
import StudentsNewEnrollment from "./pages/StudentsNewEnrollment"
import StudentsEnrollmentInfo from "./pages/StudentsEnrollmentInfo"
import ReenrollmentPhoneCalls from "./pages/ReenrollmentPhoneCalls"
import Profile from "./pages/Profile"
import Reenrollments from "./pages/Reenrollments";
import './App.css'
import SideBar from './components/SideBar'
import Header from './components/Header'
import { ToastContainer } from 'react-toastify';

const Layout = ({ isAuthenticated }) => {
  const [isSideBarOpen, setIsSideBarOpen] = useState(false);
  const { loading } = useAuth();
  const sidebarRef = useRef(null);
  
  const handleViewSidebar = () => {
    setIsSideBarOpen(!isSideBarOpen);
  };

  const closeSideBar = () => {
    setIsSideBarOpen(false);
  }

  // Handle click outside sidebar to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isSideBarOpen && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        closeSideBar();
      }
    };

    // Only add listener on small screens (when sidebar is overlay)
    if (isSideBarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSideBarOpen]);

  if (!isAuthenticated) return <Navigate to="/signin" replace />;
  if (loading) return null; // TODO: Add a loading spinner

  return (
    <div className="h-screen flex flex-col lg:flex-row overflow-hidden">
      <ToastContainer />
      <SideBar 
        ref={sidebarRef}
        isSideBarOpen={isSideBarOpen} 
        toggleSidebar={handleViewSidebar} 
        closeSideBar={closeSideBar}
      />
      <div className={`flex flex-col flex-1 min-h-0`}>
        <Header controlSideBar={handleViewSidebar}/>
        <main className="flex-1 overflow-auto z-20 lg:z-40">
          <Outlet/>
        </main>
      </div>
    </div>
  )
}

const AppRoutes = () => {
  const { isAuthenticated, loading, user } = useAuth();
  
  useEffect(() => {
    if (user && analytics.isReady()) {
      const identifyUser = () => {
        try {
          const success = analytics.identifyUser(user.id, {
            name: user.name || user.email,
            email: user.email,
          });
          return success;
        } catch (error) {
          console.error(`Error identifying user in ${analytics.getVendorType()}:`, error);
          return false;
        }
      };

      if (!identifyUser()) {
        console.log(`${analytics.getVendorType()} not ready, retrying in 1 second...`);
        const retryTimer = setTimeout(() => {
          if (!identifyUser()) {
            console.log(`${analytics.getVendorType()} still not available after retry`);
          }
        }, 1000);
        return () => clearTimeout(retryTimer);
      }
    } else if (user && !analytics.isReady()) {
      console.log(`${analytics.getVendorType()} was not initialized, skipping user identification`);
    }
  }, [user]);
  
  if (loading) return null;

  const router = createBrowserRouter([
    {
      path: '/',
      element: <Layout isAuthenticated={isAuthenticated} />,
      children: [
        {
          index: true,
          element: <Home />
        },
        {
          path: '/profile',
          element: <Profile />
        },
        {
          path: '/reenrollments',
          element: <Reenrollments />
        },
        {
          path: '/students/registered',
          element: <StudentsRegistered />
        },
        {
          path: '/students/waiting-reenrollment',
          element: <StudentsWaitingReenrollment />
        },
        {
          path: '/students/finished-reenrollment',
          element: <StudentsFinishedReenrollment />
        },
        {
          path: '/students/new-enrollment',
          element: <StudentsNewEnrollment />
        },
        {
          path: '/students/enrollment-info',
          element: <StudentsEnrollmentInfo />
        },
        {
          path: '/students/reenrollment-phone-calls',
          element: <ReenrollmentPhoneCalls />
        },
        {
          path: '/users',
          element: <Users />
        },
        {
          path: '/groups',
          element: <Groups />
        },    
      ]
    },
    {
      path: '/signin',
      element: <Signin />
    },
    {
      path: '/signin2',
      element: <Signin2 />
    },
    {
      path: '/signup',
      element: <Signup />
    }
  ]);

  return <RouterProvider router={router} />;
};

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

export default App;